import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, of } from 'rxjs';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    finalize,
    map,
    switchMap,
    tap,
} from 'rxjs/operators';

import { PropertyService } from '../services/property.service';
import { FeatureService } from '../services/feature.service';
import { AdService } from '../services/ad.service';
import { SearchCriteriaSubject } from '../subject/search-criteria.subject';
import { PropertySearchMapper } from '../mappers/property-search.mapper';

import {
    SearchCriteria,
    SortOption,
    DEFAULT_PAGE_SIZE,
} from '../models/search-criteria.model';
import { PropertySearchCardVM } from '../models/property-search-card.vm';
import { FeatureGroupDto } from '../dto/feature.group.dto';
import { GroupResponseDto } from '../dto/group.response.dto';
import { AdInformationDto } from '../dto/ad.information.dto';

interface PageState {
    offset: number;
    pageSize: number;
}

/**
 * Orquesta TODO el caso de uso de búsqueda: criterio, paginación, sort,
 * resultados, opciones de filtro (features/offers) y estados loading/error.
 *
 * Reemplaza la lógica que estaba duplicada en `property-search-list`,
 * `filters` y `property-marketplace`. Es la única capa que toca services.
 * Los componentes son UI: consumen los `*$` y disparan los métodos.
 *
 * Provista a nivel del componente marketplace (no root) para que viva
 * solo mientras la pantalla está montada — de ahí el {@link OnDestroy}.
 */
@Injectable()
export class PropertySearchFacade implements OnDestroy {

    private readonly criteria = new BehaviorSubject<SearchCriteria>({ defaultPage: true });
    private readonly page = new BehaviorSubject<PageState>({ offset: 0, pageSize: DEFAULT_PAGE_SIZE });

    private readonly results = new BehaviorSubject<PropertySearchCardVM[]>([]);
    private readonly total = new BehaviorSubject<number>(0);
    private readonly loading = new BehaviorSubject<boolean>(false);
    private readonly error = new BehaviorSubject<string | null>(null);

    private readonly primaryFeatures = new BehaviorSubject<FeatureGroupDto[]>([]);
    private readonly secondaryFeatures = new BehaviorSubject<GroupResponseDto[]>([]);
    private readonly offers = new BehaviorSubject<AdInformationDto[]>([]);

    private readonly reload = new Subject<void>();
    private readonly subs = new Subscription();

    /** Streams públicos de solo lectura. */
    readonly results$ = this.results.asObservable();
    readonly total$ = this.total.asObservable();
    readonly loading$ = this.loading.asObservable();
    readonly error$ = this.error.asObservable();
    readonly criteria$ = this.criteria.asObservable();
    readonly page$ = this.page.asObservable();
    readonly primaryFeatures$ = this.primaryFeatures.asObservable();
    readonly secondaryFeatures$ = this.secondaryFeatures.asObservable();
    readonly offers$ = this.offers.asObservable();

    constructor(
        private readonly propertyService: PropertyService,
        private readonly featureService: FeatureService,
        private readonly adService: AdService,
        private readonly mapper: PropertySearchMapper,
        private readonly criteriaStore: SearchCriteriaSubject,
    ) {
        this.wireSearchPipeline();
        this.wireFeaturePipeline();
        this.wireExternalCriteria();
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    // ---------- Intents (los dispara la UI) ----------

    /** Cambia el segmento: resetea filtros, página y recarga. */
    setSegment(segmentId: number | null): void {
        this.criteria.next({ segmentId, defaultPage: false });
        this.resetPageAndReload();
    }

    setSort(orderOptions: SortOption | null): void {
        this.patchCriteria({ orderOptions });
    }

    setPriceRange(min: number | null, max: number | null): void {
        this.patchCriteria({
            amount1: min != null ? String(min) : null,
            amount2: max != null ? String(max) : null,
        });
    }

    setFeatureFilters(codes: string[]): void {
        this.patchCriteria({ featureFilters: codes });
    }

    /** Aplica un conjunto parcial de filtros (entrada única desde `filters`). */
    applyFilters(partial: Partial<SearchCriteria>): void {
        this.patchCriteria(partial);
    }

    /** Cambio de página del paginador (offset = row-offset). */
    changePage(offset: number, pageSize: number): void {
        this.page.next({ offset, pageSize });
        this.reload.next();
    }

    /** Limpia filtros conservando el segmento actual. */
    clear(): void {
        const segmentId = this.criteria.value.segmentId ?? null;
        this.criteria.next({ segmentId, defaultPage: false });
        this.resetPageAndReload();
    }

    refresh(): void {
        this.reload.next();
    }

    // ---------- Internos ----------

    private patchCriteria(partial: Partial<SearchCriteria>): void {
        this.criteria.next({ ...this.criteria.value, ...partial, defaultPage: false });
        this.resetPageAndReload();
    }

    private resetPageAndReload(): void {
        this.page.next({ offset: 0, pageSize: this.page.value.pageSize });
        this.reload.next();
    }

    private wireSearchPipeline(): void {
        this.subs.add(
            this.reload
                .pipe(
                    debounceTime(50),
                    tap(() => {
                        this.loading.next(true);
                        this.error.next(null);
                    }),
                    switchMap(() => {
                        const c = this.criteria.value;
                        const p = this.page.value;
                        return this.propertyService.searchProperties(c, p.offset, p.pageSize).pipe(
                            map((dto) => this.mapper.toPageVM(dto)),
                            catchError(() => {
                                this.error.next('No pudimos cargar las propiedades. Inténtalo nuevamente.');
                                return of(this.mapper.toPageVM(null));
                            }),
                            finalize(() => this.loading.next(false)),
                        );
                    }),
                )
                .subscribe((pageVm) => {
                    this.results.next(pageVm.content);
                    this.total.next(pageVm.totalElements);
                }),
        );
    }

    private wireFeaturePipeline(): void {
        this.subs.add(
            this.criteria
                .pipe(
                    map((c) => c.segmentId ?? null),
                    distinctUntilChanged(),
                )
                .subscribe((segmentId) => this.loadFilterOptions(segmentId)),
        );
    }

    private loadFilterOptions(segmentId: number | null): void {
        if (segmentId == null) {
            this.primaryFeatures.next([]);
            this.secondaryFeatures.next([]);
            this.offers.next([]);
            return;
        }
        this.subs.add(
            this.featureService.getFeaturePrimaryListBySegmentId(segmentId).subscribe({
                next: (r: FeatureGroupDto[]) => this.primaryFeatures.next(r ?? []),
                error: () => this.primaryFeatures.next([]),
            }),
        );
        this.subs.add(
            this.featureService.getValueSecondaryListBySegmentId(segmentId).subscribe({
                next: (r: GroupResponseDto[]) => this.secondaryFeatures.next(r ?? []),
                error: () => this.secondaryFeatures.next([]),
            }),
        );
        this.subs.add(
            this.adService.getOffersList(segmentId).subscribe({
                next: (r: AdInformationDto[]) => this.offers.next(r ?? []),
                error: () => this.offers.next([]),
            }),
        );
    }

    /**
     * Ingesta el `SearchCriteriaSubject` global como ENTRADA: home y
     * menu-property publican ahí el segmento/búsqueda. El facade nunca
     * escribe de vuelta, así que no hay loop.
     */
    private wireExternalCriteria(): void {
        this.subs.add(
            this.criteriaStore.searchCriteria$.subscribe((raw) => this.ingestExternal(raw)),
        );
    }

    private ingestExternal(raw: any): void {
        if (!raw || raw.defaultPage) {
            this.criteria.next({ defaultPage: true });
            this.resetPageAndReload();
            return;
        }
        this.criteria.next({
            defaultPage: false,
            segmentId: raw.segmentId ?? null,
            adId: raw.adId ?? null,
            cityId: raw.cityId ?? null,
            zoneId: raw.zoneId ?? null,
            amount1: raw.amount1 ?? (raw.minPrice != null ? String(raw.minPrice) : null),
            amount2: raw.amount2 ?? (raw.maxPrice != null ? String(raw.maxPrice) : null),
            surface1: raw.surface1 ?? null,
            surface2: raw.surface2 ?? null,
            featureFilters: raw.featureFilters ?? [],
            orderOptions: raw.orderOptions ?? null,
        });
        this.resetPageAndReload();
    }
}
